import type { Faro } from '@grafana/faro-web-sdk';
import { getWebInstrumentations, initializeFaro, LogLevel } from '@grafana/faro-web-sdk';
import type { App } from 'vue';
import type { Router } from 'vue-router';

const enabled = Boolean(import.meta.env.VITE_ENABLE_GRAFANA_FARO);
const stage = import.meta.env.VITE_STAGE;
const client = import.meta.env.VITE_CLIENT;

const supportedClientStageCombinations: Record<string, string[]> = {
  Premialab: ['dev', 'test', 'uat', 'prod'],
  HSBC: ['uat', 'prod'],
  Barclays: ['uat', 'prod'],
  BofA: ['uat', 'prod'],
  RBC: ['uat', 'prod'],
  SocGen: ['uat', 'prod'],
};

const faroCollectorIds: Partial<Record<`${typeof client}-${typeof stage}`, string>> = {
  'Premialab-dev': 'b0cfa7b3bec66cd6b8e46bf87dd2371d',
  'Premialab-test': '610e69e3dedb657eef9fbb6f8c1275d1',
  'Premialab-uat': '553ce3669514485f32219c3d20376ee1',
  'Premialab-prod': 'b90cdb8e674d93da0b3b914410935ea8',
  'BofA-uat': 'a0be24a17a9fe04f7b641f7bcea5ef52',
  'BofA-prod': 'f8f714080ea2c6e7c6481aa6b5591ea7',
  'Barclays-uat': '4a477751a23d2787b72c245df6623143',
  'Barclays-prod': '5abe83f411b6b0e3d099f3a32aada136',
  'HSBC-uat': 'efd65a570f0db6c84c04f86c0b2c2473',
  'HSBC-prod': '6338c74cccce21b6acd832233e63f170',
  'RBC-uat': '919a04422046e845f8590a7fd5eac7f0',
  'RBC-prod': '6a8bcb1924748dea16a401aef83002b1',
  'SocGen-uat': 'b37f851daf492fdf014afb5f86a5e2c4',
  'SocGen-prod': 'eb03980491787cd0e224815e9ad990ad',
} as const;

// Export Faro instance so it can be accessed elsewhere
export let faroInstance: Faro | null = null;

function shouldInitializeFaro(): boolean {
  return Boolean(enabled && supportedClientStageCombinations[client]?.includes(stage));
}

// Cold-load phases are pushed synchronously during boot, before the async
// tracing import resolves and faroInstance exists. Buffer them and flush once
// Faro is ready so boot:mounted / seg:* marks are not silently lost.
type PendingPageLoadPhase = {
  phase: string;
  durationMs: number;
  extra: Record<string, string | undefined>;
  additionalValues: Record<string, number | undefined>;
};

const pendingPageLoadPhases: PendingPageLoadPhase[] = [];
const MAX_PENDING_PAGE_LOAD_PHASES = 50;
let faroInitSettled = false;

/**
 * Initialize Grafana Faro when enabled for this client/stage.
 *
 * Tracing is loaded separately so a failed `@grafana/faro-web-tracing` fetch
 * (common in Firefox while Vite is still optimizing deps) cannot blank the app.
 */
export async function initializeFaroIfEnabled(): Promise<void> {
  if (!shouldInitializeFaro()) {
    faroInitSettled = true;
    return;
  }

  // If the client is Premialab use core-${stage} as the app name, else use ${client}-${stage} as the app name
  const appName = `${client}-${stage}` as const;
  const faroUrl = `https://${location.host}/_integration/faro/collect/${faroCollectorIds[appName]}`;
  const instrumentations = [...getWebInstrumentations()];

  try {
    const { TracingInstrumentation } = await import('@grafana/faro-web-tracing');
    instrumentations.push(new TracingInstrumentation());
  } catch (error) {
    console.error('Failed to load Grafana Faro tracing instrumentation', error);
  }

  try {
    faroInstance = initializeFaro({
      url: faroUrl,
      app: {
        name: appName.replace('Premialab', 'core'),
        version: [import.meta.env.VITE_GH_REF_NAME ?? 'local', import.meta.env.VITE_GH_SHA?.slice(0, 7)]
          .filter(Boolean)
          .join('+'),
        environment: stage,
      },
      sessionTracking: {
        samplingRate: 1,
      },
      instrumentations,
      ignoreUrls: [
        'https://3da28fac89004fcc98b584edb216e03a.apm.eu-west-1.aws.cloud.es.io/intake/v2/rum/events',
        /.*\.apm\..*/,
      ],
    });
  } catch (error) {
    console.error('Failed to initialize Grafana Faro', error);
    faroInitSettled = true;
    pendingPageLoadPhases.length = 0;
    return;
  }

  faroInitSettled = true;
  flushPendingPageLoadPhases();
}

export function bindFaroToRouter(router: Router): void {
  // Set up router navigation tracking
  router.beforeEach((to, from, next) => {
    if (!faroInstance) {
      next();
      return;
    }

    try {
      faroInstance.api.pushLog([`Navigation: ${from.path} -> ${to.path}`], {
        level: LogLevel.INFO,
        context: {
          from: from.path,
          to: to.path,
          routeName: to.name?.toString() || 'Unknown',
          routeParams: JSON.stringify(to.params),
          routeQuery: JSON.stringify(to.query),
        },
      });
    } catch (error) {
      // Silently fail - telemetry should not block navigation
      console.error('Failed to log navigation to Grafana Faro:', error);
    }

    try {
      // Name the destination view for events after router guards (e.g. page:mounted).

      // This hook is registered after Router.ts guards, so it does not wrap GetUserInfo.
      // Auth duration is page_load_phase auth:get_user_info, not Faro view.
      faroInstance.api.setView({
        name: to.name?.toString() || to.path,
      });
    } catch (error) {
      // Silently fail - telemetry should not block navigation
      console.error('Failed to set the view on Grafana Faro:', error);
    }

    next();
  });

  router.afterEach((to, from) => {
    if (!faroInstance) {
      return;
    }

    try {
      // Log navigation completion
      faroInstance.api.pushLog([`Navigation completed: ${to.path}`], {
        level: LogLevel.DEBUG,
        context: {
          from: from.path,
          to: to.path,
          routeName: to.name?.toString() || 'Unknown',
        },
      });
    } catch (error) {
      // Silently fail - telemetry should not block navigation
      console.error('Failed to log navigation completion to Grafana Faro:', error);
    }
  });
}

/**
 * Set up Vue error handler to capture Vue component errors and send them to Grafana Faro.
 */
export function setupFaroVueErrorHandler(app: App<Element>) {
  const existingErrorHandler = app.config.errorHandler;

  app.config.errorHandler = (err, instance, info) => {
    // Call existing error handler first if one exists
    if (existingErrorHandler) {
      try {
        existingErrorHandler(err, instance, info);
      } catch (error) {
        // Silently fail - don't let error handler errors break the app
        console.error('Error in existing error handler:', error);
      }
    }

    if (!faroInstance) {
      // Vue does not console-log once a custom errorHandler is installed.
      if (!existingErrorHandler) {
        console.error(err);
      }

      return;
    }

    // Send error to Grafana Faro
    try {
      faroInstance.api.pushError(err as Error, {
        context: {
          componentName: instance?.$options.name || 'Unknown',
          errorInfo: info,
          vueComponent: (instance?.$options as { __file?: string })?.__file || 'Unknown',
        },
      });
    } catch (error) {
      // Silently fail - telemetry should not break error handling
      console.error('Failed to send error to Grafana Faro:', error);
    }
  };
}

type ChromiumPerformance = Performance & {
  memory?: {
    usedJSHeapSize: number;
  };
};

export type PageLoadMemorySnapshot = {
  used_js_heap_bytes?: number;
  dom_node_count: number;
};

export function getPageLoadMemorySnapshot(): PageLoadMemorySnapshot {
  const snapshot: PageLoadMemorySnapshot = {
    dom_node_count: document.getElementsByTagName('*').length,
  };

  const usedJsHeapBytes = (performance as ChromiumPerformance).memory?.usedJSHeapSize;
  if (usedJsHeapBytes != undefined) {
    snapshot.used_js_heap_bytes = usedJsHeapBytes;
  }

  return snapshot;
}

function getHasCallerMemorySnapshot(
  extra: Record<string, string | undefined>,
  additionalValues: Record<string, number | undefined>,
): boolean {
  return extra.dom_node_count != undefined || additionalValues.dom_node_count != undefined;
}

/**
 * Faro wants numbers in values and strings in context
 */
export function convertValuesToContext(values: Record<string, number>): Record<string, string> {
  return Object.fromEntries(Object.entries(values).map(([key, value]) => [key, value.toString()]));
}

/**
 * Allow Grafana to group by phase
 */
export function pushPageLoadPhase(
  phase: string,
  durationMs: number,
  extra: Record<string, string | undefined> = {},
  additionalValues: Record<string, number | undefined> = {},
): void {
  if (!faroInstance) {
    // Faro initializes asynchronously after boot. Buffer pre-init phases for
    // the flush on ready; drop them once init has settled (disabled/failed).
    if (!faroInitSettled && pendingPageLoadPhases.length < MAX_PENDING_PAGE_LOAD_PHASES) {
      pendingPageLoadPhases.push({ phase, durationMs, extra, additionalValues });
    }

    return;
  }

  sendPageLoadPhase(phase, durationMs, extra, additionalValues);
}

function flushPendingPageLoadPhases(): void {
  for (const pending of pendingPageLoadPhases.splice(0)) {
    sendPageLoadPhase(pending.phase, pending.durationMs, pending.extra, pending.additionalValues);
  }
}

function sendPageLoadPhase(
  phase: string,
  durationMs: number,
  extra: Record<string, string | undefined>,
  additionalValues: Record<string, number | undefined>,
): void {
  if (!faroInstance) return;

  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  const snapshot = getHasCallerMemorySnapshot(extra, additionalValues) ? {} : getPageLoadMemorySnapshot();

  try {
    faroInstance.api.pushMeasurement(
      {
        type: 'page_load_phase',
        values: { duration_ms: durationMs, ...snapshot, ...additionalValues },
      },
      {
        context: {
          phase,
          pathname: location.pathname,
          navigation_type: navigation?.type ?? 'unknown',
          ...convertValuesToContext(snapshot),
          ...extra,
        },
      },
    );
  } catch (error) {
    console.error('Failed to send page load phase to Grafana Faro:', error);
  }
}
